import { Region } from '@/store/slices/createThoughtspotSlice';
import { ArrowRightFromLine, UserCog, UserRound } from '@alchemer/ui/icons';
import { FlagAU, FlagCA, FlagEU, FlagSharedDev, FlagStage6, FlagStage8, FlagUS } from './UserMenuDialogFlags';
import { tw } from '@/lib/tailwind';

export type UserAvatarParams = {
  user?: {
    nickname?: string;
    email?: string;
    picture?: string;
    name?: string;
  };
  onLogout: () => void;
  onSupportAccess: () => void;
  onImpersonateUser: () => void;
  onUserPreference: () => void;
  isImpersonator?: boolean;
};

export type UserRegionParams = {
  regions: Region[];
  onChangeRegion: (r: Region) => void;
  selectedRegion: Region;
};

export type UserMenuDialogMenuItemType = {
  label: string;
  subLabel?: string;
  ariaLabel: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  isSelected?: boolean;
};

export type UserMenuDialogConfigType = {
  userAvatar: (params: UserAvatarParams) => UserMenuDialogMenuItemType[];
  userRegion: (params: UserRegionParams) => UserMenuDialogMenuItemType[];
};

export const getFlag = (region: Region) => {
  switch (region) {
    case 'us':
      return <FlagUS />;
    case 'eu':
      return <FlagEU />;
    case 'ca':
      return <FlagCA />;
    case 'au':
      return <FlagAU />;
    case 'shared-dev':
      return <FlagSharedDev />;
    case 'staging6':
      return <FlagStage6 />;
    case 'staging8':
      return <FlagStage8 />;
  }
};

const getRegionName = (region: Region) => {
  switch (region) {
    case 'us':
      return 'North America';
    case 'eu':
      return 'Europe';
    case 'ca':
      return 'Canada';
    case 'au':
      return 'Australia';
    case 'shared-dev':
      return 'Shared Development';
    case 'staging6':
      return 'Staging 6';
    case 'staging8':
      return 'Staging 8';
  }
};

export const getUserMenuDialogConfig: UserMenuDialogConfigType = {
  userAvatar: ({ user, onLogout, onSupportAccess, onImpersonateUser, onUserPreference, isImpersonator }) =>
    [
      {
        label: user?.nickname || '',
        subLabel: user?.email,
        ariaLabel: 'User profile',
        icon: (
          <img
            src={user?.picture}
            alt={user ? `${user.name}'s Avatar` : 'User Avatar'}
            className="rounded-full"
          />
        ),
        onClick: onUserPreference,
      },
      isImpersonator && {
        label: 'Impersonate a User',
        subLabel: 'Search for a user by email to impersonate',
        ariaLabel: 'Impersonate a user',
        icon: <UserRound className="h-6! w-6!" />,
        onClick: onImpersonateUser,
      },
      !isImpersonator && {
        label: 'Allow Support Access',
        subLabel: 'Lets support staff view your account',
        ariaLabel: 'Allow support access',
        icon: <UserCog className="h-6! w-6!" />,
        onClick: onSupportAccess,
      },
      {
        label: 'Log Out',
        subLabel: 'Sign out of account',
        ariaLabel: 'Sign out',
        icon: <ArrowRightFromLine className="h-6! w-6!" />,
        onClick: onLogout,
      },
    ].filter(Boolean) as UserMenuDialogMenuItemType[],

  userRegion: ({ regions, onChangeRegion, selectedRegion }) =>
    regions.map((region) => ({
      label: getRegionName(region),
      subLabel: region === selectedRegion ? 'Current region' : 'Switch to this region',
      ariaLabel: getRegionName(region),
      icon: <span className={tw('flex h-6 w-6 items-center justify-center')}>{getFlag(region)}</span>,
      onClick: () => onChangeRegion(region),
      isSelected: region === selectedRegion,
    })),
};
